import React from 'react';
import { Link } from 'react-router-dom';
import { Card, GuideMediaComposition, useGuideTheme } from './guideBlocks';

interface JourneyStep {
  role: string;
  title: string;
  body: string;
  links: { label: string; to: string }[];
  note?: string;
}

const STEPS: JourneyStep[] = [
  {
    role: 'Host',
    title: 'Create',
    body: 'Set the event type, meetup, plan and privacy, then publish.',
    links: [{ label: 'Create an event', to: '/help#create-event' }],
  },
  {
    role: 'Rider',
    title: 'Join',
    body: 'Check the plan and requirements, choose your motorcycle and RSVP. Joining records interest, not attendance.',
    links: [
      { label: 'Find and join', to: '/help#join-event' },
      { label: 'Add a motorcycle', to: '/help#add-motorcycle' },
    ],
  },
  {
    role: 'Host and riders',
    title: 'Check in',
    body: 'Use the check-in options available for the event when the app says you are eligible. Hosts confirm their own arrival and can manage riders’ check-ins.',
    links: [
      { label: 'Check in', to: '/help#check-in' },
      { label: 'Manage participants', to: '/help#manage-participants' },
    ],
  },
  {
    role: 'Host starts, riders participate',
    title: 'Take part',
    body: 'For a ride event, the checked-in host uses Start Event and confirms the start. Riders take part after check-in; RSVP alone is not completed attendance.',
    links: [
      { label: 'Event chat', to: '/help#host-chat' },
      { label: 'GroupPulse', to: '/help#grouppulse' },
    ],
    note: 'A Pro host enables it for a riding event. Each rider chooses how they share; it is not required to take part.',
  },
  {
    role: 'Host',
    title: 'Complete',
    body: 'While the event is in progress, the host uses End Event to mark it completed. Riders do not complete the host’s event themselves.',
    links: [{ label: 'Achievements', to: '/help#achievements' }],
  },
  {
    role: 'Eligible attendees and host',
    title: 'Give feedback',
    body: 'Open the completed event and use Leave a Review when offered. Review only the event and people you shared it with; feedback is optional.',
    links: [{ label: 'Feedback and reputation', to: '/social#reputation' }],
  },
];

const EventJourneyGuide: React.FC = () => {
  const { dsColors, heroPrimary, heroSecondary } = useGuideTheme();
  return (
    <>
      <p className="text-sm mb-6 max-w-2xl" style={{ color: heroSecondary, textWrap: 'pretty' }}>
        This route illustrates a ride event. Other event categories can have different arrival, start and participation controls.
      </p>
      <ol className="space-y-5">
        {STEPS.map((step, i) => (
          <li key={step.title} className="flex gap-4 items-start">
            <span
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm text-white"
              style={{ backgroundColor: dsColors.primary }}
              aria-hidden="true"
            >
              {i + 1}
            </span>
            <div className="min-w-0 w-full">
              <p className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: heroSecondary }}>{step.role}</p>
              <h3 className="font-semibold text-base mb-0.5" style={{ color: heroPrimary }}>{step.title}</h3>
              <p className="text-sm" style={{ color: heroSecondary, textWrap: 'pretty' }}>{step.body}</p>
              {step.note && (
                <p className="text-sm mt-1.5" style={{ color: heroSecondary, textWrap: 'pretty' }}>{step.note}</p>
              )}
              <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {step.links.map((l) => (
                  <Link
                    key={l.label}
                    to={l.to}
                    className="text-sm font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                    style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
                  >
                    {l.label} →
                  </Link>
                ))}
              </p>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-10">
        <GuideMediaComposition
          context={
            <>
              <p className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: heroSecondary }}>Participant history</p>
              <h3 className="font-semibold text-base mb-0.5" style={{ color: heroPrimary }}>Find your past events</h3>
              <p className="text-sm mt-1" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                From your participant event list, open the archive icon to see Archived Events. Cancelled events are read-only. For an event you archived yourself, choose Unarchive to return it to your list.
              </p>
              <p className="text-sm mt-2" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Cancellation and self-archiving do not mark an event completed or prove attendance.
              </p>
            </>
          }
          shot={{
            base: 'snapshots/20261005T052529Z/Events/iOS - Events - Participant Archive',
            alt: 'RidePulse participant archive showing a cancelled read-only event and a self-archived event with an Unarchive control',
            caption: 'Cancelled: read-only. Self-archived: Unarchive.',
          }}
        />
      </div>
      <Card className="mt-8">
        <h3 className="text-base font-bold mb-2" style={{ color: heroPrimary }}>Feedback eligibility and timing</h3>
        <p className="text-sm leading-relaxed mb-2" style={{ color: heroSecondary, textWrap: 'pretty' }}>
          Event and host feedback depends on recorded attendance, not RSVP alone. Checked-in, active and left-early states count as attended. Hosts can rate eligible riders, but not themselves as host.
        </p>
        <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
          Feedback opens after the event is recorded as completed. Use the availability and deadline shown in the app; once its eligibility window closes, submission is unavailable. Submission and public reputation display are separate, and display can be delayed or withheld.
        </p>
      </Card>
    </>
  );
};

export default EventJourneyGuide;
